import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Stack } from 'react-bootstrap';
import api from '../../api/api';
import AppHeader from '../Common/AppHeader';
import AppFooter from '../Common/AppFooter';
import { showToast } from '../../utils/toast';

const ProjectAcademicProfile = () => {
  const { slug, projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- 1. Fetch Comprehensive Academic Profile ---
  useEffect(() => {
    const fetchFullProfile = async () => {
      try {
        const res = await api.get(`/tenant/${slug}/projects/${projectId}/full-profile`);
        setProject(res.data);
      } catch (err) {
        console.error("Failed to load academic profile", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFullProfile();
  }, [projectId, slug]);

  // --- 2. Handle Document Downloads ---
  const handleDownload = async (format) => {
    try {
      const response = await api.get(`/tenant/${slug}/projects/${projectId}/download`, {
        params: { format },
        responseType: 'blob' 
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Academic_Profile_${projectId}.${format}`);
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error("Download failed:", err);
      showToast("Failed to download the document. Please ensure the backend endpoint is correct.", 'error');
    }
  };

  // --- 3. Robust Data Parser (🌟 UPDATED FOR THE HIDDEN BACKPACK) ---
  const getParsedData = () => {
    if (!project) return { modules: [], cos: {}, pos: {}, psos: {}, summary: "", references: [] };
    
    // Parse Curriculum
    let raw = project.curriculum_draft;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (e) { raw = []; }
    }
    
    // Parse References safely from DB
    let parsedRefs = project.reference_books;
    if (typeof parsedRefs === 'string') {
      try { parsedRefs = JSON.parse(parsedRefs); } catch (e) { parsedRefs = []; }
    }
    if (!Array.isArray(parsedRefs)) parsedRefs = [];

    // Fallback: Check if references exist inside the AI curriculum draft
    if (parsedRefs.length === 0 && raw.references && Array.isArray(raw.references)) {
      parsedRefs = raw.references;
    }

    // Helper: Convert React Arrays [{code: 'CO1', text: '...'}] to Dictionaries {'CO1': '...'} for UI rendering
    const listToDict = (arr) => {
      if (!Array.isArray(arr)) return arr || {};
      return arr.reduce((acc, curr) => {
        if (curr.code && curr.text) acc[curr.code] = curr.text;
        return acc;
      }, {});
    };

    // 🌟 If it's the flat array (which our editor saves)
    if (Array.isArray(raw)) {
      let cos = {}, pos = {}, psos = {}, summary = project.course_summary || "";
      
      // Look for the hidden backpack in the first module
      if (raw.length > 0 && raw[0]._outcomes_backup) {
        const backup = raw[0]._outcomes_backup;
        cos = listToDict(backup.courseOutcomes);
        pos = listToDict(backup.programOutcomes);
        psos = listToDict(backup.programSpecificOutcomes);
        if (backup.courseSummary) summary = backup.courseSummary;
      }
      
      return { modules: raw, cos, pos, psos, summary, references: parsedRefs };
    }
    
    // 🌟 If it's the Dictionary format (future-proofing)
    return {
      modules: raw.curriculum || [],
      cos: raw.course_outcomes || {},
      pos: raw.program_outcomes || {},
      psos: raw.program_specific_outcomes || {},
      summary: raw.course_summary || project.course_summary || "",
      references: parsedRefs
    };
  };

  if (loading || !project) return <div className="text-center py-5"><Spinner animation="border" variant="primary" aria-hidden="true" /></div>;

  const { modules, cos, pos, psos, summary, references } = getParsedData();
  const hasOutcomes = Object.keys(cos).length > 0 || Object.keys(pos).length > 0 || Object.keys(psos).length > 0;

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      
      <Container className="py-5 flex-grow-1">
        
        {/* --- Header Section --- */}
        <div className="d-flex justify-content-between align-items-end mb-4 flex-wrap gap-3">
          <div>
            <Badge bg="primary" className="mb-2 text-uppercase">Academic Record of Authority</Badge>
            <h2 className="fw-bold mb-0">{project.name}</h2>
            <p className="text-muted mb-0">
              {project.tenant_name || 'Institution'} | {project.program_name}
            </p>
          </div>
          <Stack direction="horizontal" gap={2}>
            <Button variant="outline-danger" size="sm" onClick={() => handleDownload('pdf')}>
              <i className="bi bi-file-pdf me-2"></i>Export PDF
            </Button>
            <Button variant="outline-primary" size="sm" onClick={() => handleDownload('docx')}>
              <i className="bi bi-file-word me-2"></i>Export DOCX
            </Button>
            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>Back to Dashboard</Button>
          </Stack>
        </div>

        {/* --- Metadata Grid --- */}
        <Row className="g-4 mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center p-3 h-100">
              <small className="text-muted fw-bold">ACADEMIC LEVEL</small>
              <div className="fw-bold text-dark mt-1">{project.program_level}</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center p-3 h-100">
              <small className="text-muted fw-bold">TAXONOMY STANDARD</small>
              <div className="fw-bold text-dark mt-1">{project.taxonomy_name}</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center p-3 h-100">
              <small className="text-muted fw-bold">GROUNDING MODE</small>
              <div className="fw-bold text-primary mt-1">{project.curriculum_mode?.replace('_', ' ').toUpperCase()}</div>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center p-3 h-100 border-top border-4 border-success">
              <small className="text-muted fw-bold">GRAPH STATUS</small>
              <div className={`fw-bold mt-1 ${project.ckb_status === 'completed' ? 'text-success' : 'text-warning'}`}>
                {project.ckb_status === 'completed' ? (
                  <><i className="bi bi-check-circle-fill me-1"></i>Extraction Ready</>
                ) : (
                  <><Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />Processing...</>
                )}
              </div>
            </Card>
          </Col>
        </Row>

        {/* --- Course Overview & Framework --- */}
        {(summary || hasOutcomes) && (
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white fw-bold py-3 border-bottom">
              <i className="bi bi-diagram-3 me-2 text-primary"></i>Course Rationale & OBE Framework
            </Card.Header>
            <Card.Body>
              {summary && (
                <div className="mb-4">
                  <h6 className="fw-bold text-muted text-uppercase small">Course Summary</h6>
                  <p className="mb-0">{summary}</p>
                </div>
              )}
              
              <Row>
                {Object.keys(cos).length > 0 && (
                  <Col md={4}>
                    <h6 className="fw-bold text-primary text-uppercase small border-bottom pb-2">Course Outcomes (CO)</h6>
                    <ul className="list-unstyled small">
                      {Object.entries(cos).map(([code, text]) => (
                        <li key={code} className="mb-2"><strong>{code}:</strong> {text}</li>
                      ))}
                    </ul>
                  </Col>
                )}
                {Object.keys(pos).length > 0 && (
                  <Col md={4}>
                    <h6 className="fw-bold text-success text-uppercase small border-bottom pb-2">Program Outcomes (PO)</h6>
                    <ul className="list-unstyled small">
                      {Object.entries(pos).map(([code, text]) => (
                        <li key={code} className="mb-2"><strong>{code}:</strong> {text}</li>
                      ))}
                    </ul>
                  </Col>
                )}
                {Object.keys(psos).length > 0 && (
                  <Col md={4}>
                    <h6 className="fw-bold text-info text-uppercase small border-bottom pb-2">Program Specific (PSO)</h6>
                    <ul className="list-unstyled small">
                      {Object.entries(psos).map(([code, text]) => (
                        <li key={code} className="mb-2"><strong>{code}:</strong> {text}</li>
                      ))}
                    </ul>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        )}

        {/* --- RESTORED: Reference Books Section --- */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between">
            <span><i className="bi bi-bookmark-check me-2 text-success"></i>Validated Source References</span>
            <Badge bg="light" text="dark" className="border">{references.length} Asset(s)</Badge>
          </Card.Header>
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 small">
              <thead className="table-light">
                <tr><th>Title</th><th>Author / Publisher</th><th>Scope</th></tr>
              </thead>
              <tbody>
                {references.length > 0 ? (
                  references.map((b, i) => (
                    <tr key={i}>
                      <td className="fw-bold">{b.title || b.fileName || 'Untitled Asset'}</td>
                      <td>{b.author || b.publisher || 'N/A'}</td>
                      <td>{b.topic_title || b.relevant_pages || b.chapters || 'Complete Work'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-4 text-muted fst-italic">
                      No textbook references mapped. Utilizing faculty-uploaded assets only.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* --- The Full Curriculum Mapping --- */}
        <Card className="border-0 shadow-sm mb-5">
          <Card.Header className="bg-dark text-white fw-bold py-3">
            Approved Curriculum & Learning Outcomes
          </Card.Header>
          <Table responsive bordered className="mb-0 align-middle">
            <thead className="table-light small text-uppercase">
              <tr>
                <th scope="col" style={{ width: '22%' }}>Structure</th>
                <th scope="col" style={{ width: '48%' }}>Description / Topics</th>
                <th scope="col" className="text-center" style={{ width: '15%' }}>Cognitive Level</th>
                <th scope="col" className="text-center" style={{ width: '15%' }}>Accreditation</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((mod, mIdx) => (
                <React.Fragment key={mIdx}>
                  
                  {/* Module Header Row */}
                  <tr className="table-secondary fw-bold">
                    <td colSpan="2" className="py-3 text-uppercase text-primary" style={{ letterSpacing: '0.5px', fontSize: '1.05rem' }}>
                      {mod.title}
                    </td>
                    <td colSpan="2" className="text-end pe-3">
                       {mod.module_average_cognitive_score && (
                         <Badge bg="info" text="dark" className="border shadow-sm">
                           Avg. Cognitive Score: {mod.module_average_cognitive_score}
                         </Badge>
                       )}
                    </td>
                  </tr>

                  {/* Module Details (Description & Skills) */}
                  {(mod.description || mod.skills_competencies) && (
                    <tr className="bg-light">
                      <td colSpan="4" className="small">
                        {mod.description && <div className="mb-2"><strong>Rationale:</strong> {mod.description}</div>}
                        {mod.skills_competencies && <div><strong>Target Skills:</strong> <span className="text-muted">{mod.skills_competencies}</span></div>}
                      </td>
                    </tr>
                  )}

                  {/* Learning Outcomes Row */}
                  {mod.learning_outcomes?.map((lo, loIdx) => {
                    const loText = lo.text || lo.learning_outcome;
                    const bloom = lo.bloom_level || lo.blooms_level || '';
                    const mappings = [lo.mapped_co, lo.mapped_po, lo.mapped_pso].filter(Boolean).join(', ');
                    
                    return (
                      <tr key={`lo-${loIdx}`}>
                        <td className="ps-4 text-success fw-bold small"><i className="bi bi-bullseye me-2"></i>LO {loIdx + 1}</td>
                        <td className="small">{loText}</td>
                        <td className="text-center">
                          {bloom && (
                            <Badge bg="primary" style={{ fontSize: '0.75rem' }}>
                              {bloom.startsWith('L') ? bloom : `L-${bloom}`}
                            </Badge>
                          )}
                        </td>
                        <td className="text-center small fw-bold text-secondary">
                          {mappings || '-'}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Units and Topics Row */}
                  {mod.children?.map((unit, uIdx) => {
                     const topics = unit.topics?.map(t => typeof t === 'string' ? t : (t.title || t.name)) || [];
                     return (
                      <tr key={`unit-${uIdx}`}>
                        <td className="ps-4 fw-semibold text-dark"><i className="bi bi-journal-text me-2 text-muted"></i>{unit.title}</td>
                        <td className="small text-muted">
                           {topics.length > 0 ? (
                             <ul className="mb-0 ps-3">
                               {topics.map((t, i) => <li key={i}>{t}</li>)}
                             </ul>
                           ) : (
                             <em>No topics defined</em>
                           )}
                        </td>
                        <td className="text-center text-muted">-</td>
                        <td className="text-center text-muted">-</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </Card>
      </Container>
      <AppFooter />
    </div>
  );
};

export default ProjectAcademicProfile;